<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { dynamicTime } from 'shared/helpers/timeHelper';
import SearchResultSection from './SearchResultSection.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  channels: { type: Array, default: () => [] },
  dms: { type: Array, default: () => [] },
  messages: { type: Array, default: () => [] },
  query: { type: String, default: '' },
  isFetching: { type: Boolean, default: false },
  showTitle: { type: Boolean, default: true },
  hasMore: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const totalCount = computed(
  () => props.channels.length + props.dms.length + props.messages.length
);

function navigateToChannel(channel) {
  const routeName =
    channel.channel_type === 'dm' ? 'internal_chat_dm' : 'internal_chat_channel';
  router.push({
    name: routeName,
    params: { accountId: route.params.accountId, channelId: channel.id },
  });
}

function navigateToMessage(message) {
  const routeName =
    message.channel_type === 'dm' ? 'internal_chat_dm' : 'internal_chat_channel';
  const query = { messageId: message.id };
  if (message.parent_id) query.parentId = message.parent_id;
  router.push({
    name: routeName,
    params: { accountId: route.params.accountId, channelId: message.channel_id },
    query,
  });
  emitter.emit('internal-chat:jump-to-message', {
    channelId: message.channel_id,
    messageId: message.id,
    parentId: message.parent_id || null,
  });
}

function getChannelIcon(channel) {
  if (channel.channel_type === 'private_channel') return 'i-lucide-lock';
  return 'i-lucide-hash';
}

function normalizeText(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function stripMarkup(text) {
  if (!text) return '';
  let clean = text.replace(/\[@([^\]]*)\]\(mention:\/\/[^)]*\)/g, '@$1');
  clean = clean.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  clean = clean.replace(/<[^>]+>/g, '');
  return clean;
}

function truncateAroundMatch(text, query, maxLen = 160) {
  if (!text) return '';
  const idx = normalizeText(text).indexOf(normalizeText(query));
  if (idx === -1 || text.length <= maxLen) return text;
  const start = Math.max(0, idx - Math.floor(maxLen / 2));
  const end = Math.min(text.length, start + maxLen);
  let snippet = text.slice(start, end);
  if (start > 0) snippet = `...${snippet}`;
  if (end < text.length) snippet = `${snippet}...`;
  return snippet;
}

function highlightMatch(text, query) {
  if (!query || !text) return escapeHtml(text || '');
  const clean = stripMarkup(text);
  const snippet = truncateAroundMatch(clean, query);
  const normSnippet = normalizeText(snippet);
  const normQuery = normalizeText(query);
  if (!normQuery) return escapeHtml(snippet);
  const parts = [];
  let start = 0;
  let idx = normSnippet.indexOf(normQuery);
  while (idx !== -1) {
    if (idx > start) parts.push(escapeHtml(snippet.slice(start, idx)));
    const matchEnd = idx + normQuery.length;
    parts.push(`<mark>${escapeHtml(snippet.slice(idx, matchEnd))}</mark>`);
    start = matchEnd;
    idx = normSnippet.indexOf(normQuery, start);
  }
  if (start < snippet.length) parts.push(escapeHtml(snippet.slice(start)));
  return parts.join('');
}

function messageTime(createdAt) {
  if (!createdAt) return '';
  return dynamicTime(
    typeof createdAt === 'number' ? createdAt : Math.floor(new Date(createdAt).getTime() / 1000)
  );
}
</script>

<template>
  <SearchResultSection
    :title="t('INTERNAL_CHAT.TITLE')"
    :empty="totalCount === 0"
    :query="query"
    :show-title="showTitle"
    :is-fetching="isFetching"
  >
    <!-- Channels -->
    <div v-if="channels.length" class="mb-3">
      <h4
        v-if="!showTitle || dms.length || messages.length"
        class="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
      >
        {{ t('INTERNAL_CHAT.SEARCH.CHANNELS') }}
      </h4>
      <ul class="list-none space-y-3">
        <li v-for="channel in channels" :key="`ch-${channel.id}`">
          <button class="w-full text-left" @click="navigateToChannel(channel)">
            <CardLayout
              layout="row"
              class="[&>div]:justify-start [&>div]:px-4 [&>div]:py-3 [&>div]:items-center hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <div
                class="flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
              >
                <Icon :icon="getChannelIcon(channel)" class="size-4 text-n-slate-11" />
              </div>
              <div class="min-w-0 flex-1">
                <h5 class="truncate text-sm font-medium text-n-slate-12">
                  {{ channel.name }}
                </h5>
                <p v-if="channel.description" class="truncate text-xs text-n-slate-10 mt-0.5">
                  {{ channel.description }}
                </p>
              </div>
              <Icon icon="i-lucide-message-square" class="size-4 flex-shrink-0 text-n-slate-9" />
            </CardLayout>
          </button>
        </li>
      </ul>
    </div>

    <!-- DMs -->
    <div v-if="dms.length" class="mb-3">
      <h4
        v-if="!showTitle || channels.length || messages.length"
        class="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
      >
        {{ t('INTERNAL_CHAT.SEARCH.DIRECT_MESSAGES') }}
      </h4>
      <ul class="list-none space-y-3">
        <li v-for="dm in dms" :key="`dm-${dm.id}`">
          <button class="w-full text-left" @click="navigateToChannel(dm)">
            <CardLayout
              layout="row"
              class="[&>div]:justify-start [&>div]:px-4 [&>div]:py-3 [&>div]:items-center hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <Avatar
                :name="dm.peer?.name || ''"
                :src="dm.peer?.avatar_url || ''"
                :size="32"
                rounded-full
                class="flex-shrink-0"
              />
              <div class="min-w-0 flex-1">
                <h5 class="truncate text-sm font-medium text-n-slate-12">
                  {{ dm.peer?.name || '' }}
                </h5>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ t('INTERNAL_CHAT.DIRECT_MESSAGES') }}
                </p>
              </div>
              <Icon icon="i-lucide-message-circle" class="size-4 flex-shrink-0 text-n-slate-9" />
            </CardLayout>
          </button>
        </li>
      </ul>
    </div>

    <!-- Messages -->
    <div v-if="messages.length" class="mb-2">
      <h4
        v-if="!showTitle || channels.length || dms.length"
        class="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
      >
        {{ t('INTERNAL_CHAT.SEARCH.MESSAGES') }}
      </h4>
      <ul class="list-none space-y-3">
        <li v-for="message in messages" :key="`msg-${message.id}`">
          <button class="w-full text-left" @click="navigateToMessage(message)">
            <CardLayout
              layout="col"
              class="[&>div]:justify-start [&>div]:gap-2 [&>div]:px-4 [&>div]:py-3 [&>div]:items-start hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
            >
              <div class="flex w-full items-center justify-between gap-2">
                <div class="flex min-w-0 items-center gap-2">
                  <Avatar
                    :name="message.sender?.name || ''"
                    :src="message.sender?.avatar_url || ''"
                    :size="20"
                    rounded-full
                    class="flex-shrink-0"
                  />
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ message.sender?.name || '' }}
                  </span>
                  <span class="h-3 w-px flex-shrink-0 rounded bg-n-strong" />
                  <span class="flex-shrink-0 text-sm text-n-slate-11">
                    {{
                      message.channel_type === 'dm'
                        ? message.channel_name || ''
                        : `#${message.channel_name || ''}`
                    }}
                  </span>
                </div>
                <span class="flex-shrink-0 text-sm text-n-slate-11">
                  {{ messageTime(message.created_at) }}
                </span>
              </div>
              <p
                class="line-clamp-2 text-sm text-n-slate-11"
                v-html="highlightMatch(message.content, query)"
              />
            </CardLayout>
          </button>
        </li>
      </ul>
      <div v-if="hasMore && !isFetching" class="mt-3 flex justify-center">
        <button
          class="text-xs text-n-slate-10 transition-colors hover:text-n-slate-12"
          @click="emit('loadMore')"
        >
          {{ t('INTERNAL_CHAT.SEARCH.LOAD_MORE') }}
        </button>
      </div>
    </div>
  </SearchResultSection>
</template>
